import { Injectable } from '@angular/core';

export const INTERESTED_IN_LAT_LON = 'INTERESTED_IN_LAT_LON';

// Explicit interest (cell click / search) stays authoritative for this long;
// after it expires a viewport-derived location may replace it.
const EXPLICIT_TTL_MS = 48 * 60 * 60 * 1000;

// ~11m precision — enough to resolve a county, avoids noisy rewrites
const COORDINATE_DECIMALS = 4;

type InterestSource = 'explicit' | 'viewport';

interface InterestEntry {
  lat: number;
  lng: number;
  source: InterestSource;
  timestamp: number;
}

/**
 * Tracks the location the user is interested in (clicked cell, search result,
 * or viewport center as a fallback).
 *
 * Written by the map, the geocoder and the sales-forecast pages; read by
 * nobody since the subscription flow's coverage-area picker - its only
 * consumer - was removed. Kept because the signal is cheap to keep and
 * expensive to reconstruct, but delete it if nothing claims it.
 */
@Injectable({
  providedIn: 'root',
})
export class UserInterestLocationService {
  // Cell click or search result — always wins over viewport, last action wins
  public setExplicit(lat: number, lng: number): void {
    this.save(lat, lng, 'explicit');
  }

  // Viewport center after pan/zoom — never overrides a fresh explicit location
  public setViewport(lat: number, lng: number): void {
    const current = this.read();
    if (
      current?.source === 'explicit' &&
      Date.now() - current.timestamp <= EXPLICIT_TTL_MS
    ) {
      return;
    }
    this.save(lat, lng, 'viewport');
  }

  public getCoordinates(): { lat: number; lng: number } | null {
    const entry = this.read();
    return entry ? { lat: entry.lat, lng: entry.lng } : null;
  }

  private save(lat: number, lng: number, source: InterestSource): void {
    const entry: InterestEntry = {
      lat: this.round(lat),
      lng: this.round(lng),
      source,
      timestamp: Date.now(),
    };
    localStorage.setItem(INTERESTED_IN_LAT_LON, JSON.stringify(entry));
  }

  private read(): InterestEntry | null {
    try {
      const raw = localStorage.getItem(INTERESTED_IN_LAT_LON);
      if (!raw) return null;

      const entry = JSON.parse(raw) as InterestEntry;
      if (typeof entry.lat !== 'number' || typeof entry.lng !== 'number') {
        return null;
      }
      return entry;
    } catch {
      localStorage.removeItem(INTERESTED_IN_LAT_LON);
      return null;
    }
  }

  private round(value: number): number {
    const factor = 10 ** COORDINATE_DECIMALS;
    return Math.round(value * factor) / factor;
  }
}
